import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  CheckCircle2, Sparkles, Gift, Clock, Calendar, BarChart3, 
  Target, Flame, MessageSquare, Share2, GripVertical, 
  PartyPopper, Trophy, User, Zap, Moon, Sun, Download,
  Shield, Star, Users, Award, Lock, CreditCard, ArrowRight
} from 'lucide-react';

const AuthPage = () => {
  const { login, register } = useAuth();
  const [activeTab, setActiveTab] = useState('login');
  
  // Login state
  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });
  
  // Register state
  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      toast.success('Welcome back!');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    const result = await register(
      registerData.email,
      registerData.password,
      registerData.name
    );
    
    if (result.success) {
      toast.success('Account created successfully!');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  const features = [
    { icon: Target, text: 'Track unlimited habits', color: 'text-blue-500' },
    { icon: Calendar, text: 'Flexible scheduling (daily or specific days)', color: 'text-purple-500' },
    { icon: GripVertical, text: 'Drag & drop to reorder habits', color: 'text-indigo-500' },
    { icon: MessageSquare, text: 'Add notes & journal entries', color: 'text-pink-500' },
    { icon: Sparkles, text: 'AI-powered daily motivational quotes', color: 'text-amber-500' },
    { icon: PartyPopper, text: 'Celebration animations & confetti', color: 'text-green-500' },
    { icon: Trophy, text: 'Progress milestones (25%, 50%, 75%, 100%)', color: 'text-orange-500' },
    { icon: Flame, text: 'Streak tracking & fire badges', color: 'text-red-500' },
    { icon: BarChart3, text: 'Beautiful analytics & charts', color: 'text-cyan-500' },
    { icon: Share2, text: 'Share progress on social media', color: 'text-blue-400' },
    { icon: User, text: 'Fun avatar customization', color: 'text-violet-500' },
    { icon: Download, text: 'Export to CSV & PDF', color: 'text-emerald-500' },
  ];

  // Customer testimonials
  const testimonials = [
    {
      name: 'Sarah M.',
      role: 'Fitness Enthusiast',
      avatar: '👩‍💼',
      rating: 5,
      text: 'Finally broke my 30-day workout streak! The celebration animations keep me motivated.',
    },
    {
      name: 'James L.',
      role: 'Software Developer',
      avatar: '👨‍💻',
      rating: 5,
      text: 'The AI quotes are surprisingly personal. It feels like having a coach in my pocket.',
    },
    {
      name: 'Priya K.',
      role: 'Student',
      avatar: '👩‍🎓',
      rating: 5,
      text: 'Helped me build a consistent study routine. The journal feature is perfect for reflection.',
    },
  ];

  // Trust badges
  const trustBadges = [
    { icon: Shield, text: 'Secure & Private', color: 'text-green-500' },
    { icon: Lock, text: 'SSL Encrypted', color: 'text-blue-500' },
    { icon: CreditCard, text: 'Safe Payments', color: 'text-purple-500' },
    { icon: Award, text: 'Cancel Anytime', color: 'text-orange-500' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl"></div>
      <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-pink-400/10 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-6 py-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          
          {/* Left Side - Features Showcase */}
          <div className="hidden lg:block">
            {/* Logo and Title */}
            <div className="mb-8">
              <div className="relative w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 flex items-center justify-center mb-4 shadow-2xl hover-glow animate-fade-in group cursor-pointer overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                <span className="relative text-white font-heading font-bold text-2xl tracking-tight z-10 group-hover:scale-110 transition-transform duration-300">
                  H2G
                </span>
              </div>
              <h1 className="font-heading text-5xl font-bold gradient-text mb-4 animate-fade-in">
                Habit2Goals
              </h1>
              <p className="text-xl text-slate-600 dark:text-slate-400 animate-fade-in">
                The smart habit tracker that makes building good habits <span className="text-purple-600 dark:text-purple-400 font-semibold">fun and rewarding</span>
              </p>
            </div>

            {/* Feature Highlights */}
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-semibold text-slate-800 dark:text-slate-100">
                Everything you need to succeed
              </h2>
              
              <div className="grid grid-cols-2 gap-3">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-white/20 dark:border-slate-700/50 hover:scale-105 transition-transform cursor-default"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <feature.icon className={`w-5 h-5 ${feature.color} flex-shrink-0`} />
                    <span className="text-sm text-slate-700 dark:text-slate-300">{feature.text}</span>
                  </div>
                ))}
              </div>

              {/* Stats/Social Proof */}
              <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
                <div className="text-center">
                  <p className="text-3xl font-bold gradient-text">7 Days</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Free Trial</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold gradient-text">US$2.99</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">/month after</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold gradient-text">∞</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">Unlimited habits</p>
                </div>
              </div>

              {/* Desktop Testimonials */}
              <div className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700" data-testid="desktop-testimonials">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-blue-500" />
                  <span className="text-base font-semibold text-slate-700 dark:text-slate-300">Loved by habit builders</span>
                </div>
                <div className="space-y-3">
                  {testimonials.map((testimonial, index) => (
                    <div 
                      key={index}
                      className="flex gap-3 p-3 rounded-xl bg-white/40 dark:bg-slate-800/40 backdrop-blur-sm border border-white/20 dark:border-slate-700/50"
                    >
                      <span className="text-2xl flex-shrink-0 mt-0.5">{testimonial.avatar}</span>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{testimonial.name}</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">{testimonial.role}</span>
                          <div className="flex gap-0.5 ml-auto">
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">"{testimonial.text}"</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Auth Forms */}
          <div className="flex flex-col items-center justify-center min-h-[calc(100vh-4rem)]">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center mb-6">
              <div className="relative w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-600 to-pink-500 flex items-center justify-center mx-auto mb-3 shadow-2xl">
                <span className="text-white font-heading font-bold text-xl tracking-tight">H2G</span>
              </div>
              <h1 className="font-heading text-3xl font-bold gradient-text mb-1">Habit2Goals</h1>
              <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-500" />
                Build better habits, achieve your goals
              </p>
            </div>

            {/* New User CTA Banner - shown on login tab */}
            {activeTab === 'login' && (
              <div 
                data-testid="new-user-cta-banner"
                className="w-full max-w-md mb-4 p-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-cyan-500/10 border border-emerald-500/20 dark:border-emerald-400/20 backdrop-blur-sm animate-fade-in cursor-pointer hover:scale-[1.02] transition-transform"
                onClick={() => setActiveTab('register')}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-emerald-700 dark:text-emerald-300 text-sm">
                      New to Habit2Goals?
                    </p>
                    <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80">
                      Start your 7-day free trial — no credit card needed
                    </p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                </div>
              </div>
            )}

            {/* Auth Card */}
            <div className="w-full max-w-md gradient-card rounded-3xl p-8 shadow-2xl animate-fade-in">
              <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="auth-tabs">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-gradient-to-r from-blue-500/10 to-purple-500/10 p-1 rounded-xl">
                  <TabsTrigger 
                    value="login" 
                    data-testid="tab-login"
                    className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
                  >
                    Login
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    data-testid="tab-register"
                    className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg"
                  >
                    Register
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="login-submit-button"
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow"
                      disabled={loading}
                    >
                      {loading ? 'Logging in...' : 'Login'}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="register">
                  {/* Free Trial Banner */}
                  <div className="mb-6 p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center flex-shrink-0">
                        <Gift className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-green-700 dark:text-green-400">
                          7-Day Free Trial Included!
                        </p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                          Full access to all features. No credit card required.
                        </p>
                      </div>
                    </div>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="register-name">Name</Label>
                      <Input
                        id="register-name"
                        data-testid="register-name-input"
                        type="text"
                        placeholder="Your name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <Input
                        id="register-email"
                        data-testid="register-email-input"
                        type="email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <Input
                        id="register-password"
                        data-testid="register-password-input"
                        type="password"
                        placeholder="At least 6 characters"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-confirm-password">Confirm Password</Label>
                      <Input
                        id="register-confirm-password"
                        data-testid="register-confirm-password-input"
                        type="password"
                        placeholder="Confirm your password"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="register-submit-button"
                      className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg hover-glow"
                      disabled={loading}
                    >
                      {loading ? 'Creating account...' : 'Start Free Trial'}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              {/* Mobile Features Section */}
              <div className="lg:hidden mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 text-center">
                  Everything you need to succeed
                </h3>
                <div className="grid grid-cols-2 gap-2">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 p-2 rounded-lg bg-white/30 dark:bg-slate-800/30"
                    >
                      <feature.icon className={`w-4 h-4 ${feature.color} flex-shrink-0`} />
                      <span className="text-xs text-slate-600 dark:text-slate-400 line-clamp-2">{feature.text}</span>
                    </div>
                  ))}
                </div>
                
                {/* Mobile Stats */}
                <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-200/50 dark:border-slate-700/50">
                  <div className="text-center">
                    <p className="text-lg font-bold gradient-text">7 Days</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Free Trial</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold gradient-text">US$2.99</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">/month</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold gradient-text">∞</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Unlimited</p>
                  </div>
                </div>
              </div>

              {/* Desktop "What you get" - hidden on mobile since we show full features above */}
              <div className="hidden lg:block mt-6 pt-6 border-t border-slate-200 dark:border-slate-700">
                <div className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                  <p className="font-medium text-slate-700 dark:text-slate-300 mb-3">What you get:</p>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    <span>AI-powered motivational quotes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-purple-500" />
                    <span>Flexible scheduling & drag-drop reorder</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-pink-500" />
                    <span>Celebration animations & streaks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Progress milestones & social sharing</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trust Badges */}
            <div data-testid="trust-badges-section" className="w-full max-w-md mt-4 grid grid-cols-4 gap-2">
              {trustBadges.map((badge, index) => (
                <div 
                  key={index}
                  data-testid={`trust-badge-${index}`}
                  className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl bg-white/40 dark:bg-slate-800/40 backdrop-blur-sm border border-white/30 dark:border-slate-700/40"
                >
                  <badge.icon className={`w-4 h-4 ${badge.color}`} />
                  <span className="text-[10px] font-medium text-slate-600 dark:text-slate-400 text-center leading-tight">{badge.text}</span>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div data-testid="testimonials-section" className="w-full max-w-md mt-6">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">What our users say</span>
              </div>
              <div className="space-y-3">
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index}
                    data-testid={`testimonial-card-${index}`}
                    className="p-3.5 rounded-xl bg-white/50 dark:bg-slate-800/50 backdrop-blur-sm border border-white/30 dark:border-slate-700/40"
                  >
                    <div className="flex items-center gap-2.5 mb-2">
                      <span className="text-lg">{testimonial.avatar}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{testimonial.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{testimonial.role}</p>
                      </div>
                      <div className="flex gap-0.5">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="w-3 h-3 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">"{testimonial.text}"</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 text-center text-xs text-slate-500 dark:text-slate-400">
              <p>© {new Date().getFullYear()} Tiara Consultants - AI Product Dev Division</p>
              <p className="mt-2">
                This app is for habit management purposes only.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
